// src/components/TodoConectado.tsx
//
// Sección de la home: "Todo conectado". Explica que las recetas electrónicas
// vienen incluidas en el plan y lo que se gana teniendo todo enlazado: sedes,
// turnos, historias clínicas, recetas, cobros y recordatorios en un sistema.
// Pedido de Esteban (oct-2026). Va justo antes de la tarjeta de precio.
//
// Escritorio (desde 1024 px): un esquema con el consultorio al centro y las
// piezas a los costados, unidas por líneas que recorren pulsos de luz (como la
// cinta del hero). Celular y tablet: las mismas piezas en columna, unidas por
// una línea (en 768 px el esquema quedaba apretado: las piezas casi tocaban
// el centro).
// Animaciones solo de transform/opacity/stroke-dashoffset, se encienden al
// entrar en pantalla y quedan quietas con prefers-reduced-motion.

import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, ClipboardList, MapPin, Pill, CreditCard, Bell, Link2, Users } from 'lucide-react';
import './TodoConectado.css';

interface Pieza {
  titulo: string;
  detalle: string;
  icono: React.ReactNode;
  // Posición en el esquema de escritorio (viewBox 960 × 440)
  x: number;
  y: number;
  destacada?: boolean;
}

const PIEZAS: Pieza[] = [
  { titulo: 'Turnos', detalle: 'Tu agenda online, las 24 horas', icono: <CalendarDays />, x: 138, y: 70 },
  { titulo: 'Pacientes e historias clínicas', detalle: 'Cada paciente con su ficha', icono: <ClipboardList />, x: 138, y: 220 },
  { titulo: 'Sedes', detalle: 'Todos los lugares donde atendés', icono: <MapPin />, x: 138, y: 370 },
  { titulo: 'Recetas electrónicas', detalle: 'Incluidas en tu plan', icono: <Pill />, x: 822, y: 70, destacada: true },
  { titulo: 'Cobros', detalle: 'Mercado Pago o transferencia', icono: <CreditCard />, x: 822, y: 220 },
  { titulo: 'Recordatorios', detalle: 'Por WhatsApp y email', icono: <Bell />, x: 822, y: 370 },
];

const BENEFICIOS = [
  {
    icono: <Pill />,
    titulo: 'La receta sale de la historia clínica',
    texto: 'Los datos del paciente ya están cargados: la receta se hace en pocos clics y queda en su ficha.',
  },
  {
    icono: <MapPin />,
    titulo: 'Cada sede, con su agenda',
    texto: 'Días, horarios y servicios de cada lugar donde atendés, en un solo panel. Tus pacientes eligen la sede al reservar.',
  },
  {
    icono: <CreditCard />,
    titulo: 'El cobro viaja con el turno',
    texto: 'La seña se paga al reservar, con Mercado Pago o con el comprobante de la transferencia, y queda en el turno.',
  },
  {
    icono: <Users />,
    titulo: 'Cada paciente, una sola ficha',
    texto: 'Sus turnos, recetas e historias clínicas juntos, a mano cuando lo atendés.',
  },
];

// Mitad del ancho de las tarjetas, en px (= unidades del esquema, que mide
// 960 de ancho): van con los width de .tc-centro y .tc-pieza del CSS. El
// centro es más ancho para que el link entre en un renglón.
const MEDIO_CENTRO = 144;
const MEDIO_PIEZA = 118;

// Curva del centro a cada pieza: sale del borde del centro y llega al borde
// interno de la tarjeta, con la panza horizontal.
const curva = (p: Pieza) => {
  const izquierda = p.x < 480;
  const xCentro = izquierda ? 480 - MEDIO_CENTRO : 480 + MEDIO_CENTRO;
  const xPieza = izquierda ? p.x + MEDIO_PIEZA : p.x - MEDIO_PIEZA;
  const xControl = (xCentro + xPieza) / 2;
  return `M${xCentro},220 C${xControl},220 ${xControl},${p.y} ${xPieza},${p.y}`;
};

const TodoConectado: React.FC<{ precio: string; alVerPlan?: () => void }> = ({ precio, alVerPlan }) => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  // Las animaciones arrancan recién cuando la sección entra en pantalla
  useEffect(() => {
    const nodo = ref.current;
    if (!nodo || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observador = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        observador.disconnect();
      }
    }, { rootMargin: '0px 0px -15% 0px' });
    observador.observe(nodo);
    return () => observador.disconnect();
  }, []);

  return (
    <section ref={ref} className={`todo-conectado relative py-20 sm:py-28 bg-slate-900 overflow-hidden${visible ? ' is-visible' : ''}`}>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="tc-sobre">Incluido en tu plan</span>
          {/* texto-balanceado (Hero.css): sin eso, "lugar" quedaba solo en el
              segundo renglón */}
          <h2 className="texto-balanceado mt-4 text-3xl sm:text-4xl md:text-5xl font-bold text-white">
            Todo conectado, en un solo lugar
          </h2>
          <p className="texto-balanceado mt-4 text-lg sm:text-xl text-slate-300 leading-relaxed">
            Turnos, historias clínicas, recetas electrónicas y cobros de todas tus sedes, en el mismo sistema.
          </p>
        </div>

        {/* Escritorio: esquema con el consultorio al centro */}
        <div className="tc-esquema hidden lg:block" aria-hidden="true">
          <svg viewBox="0 0 960 440" className="tc-lineas">
            <defs>
              {/* En coordenadas del esquema y no de cada línea: la línea del
                  medio es recta, no tiene alto, y con las coordenadas de la
                  línea el navegador no pinta el degradé (sus pulsos no se
                  veían). */}
              <linearGradient id="tc-trazo" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="960" y2="0">
                <stop offset="0" stopColor="#38bdf8" />
                <stop offset="0.5" stopColor="#22d3ee" />
                <stop offset="1" stopColor="#34d399" />
              </linearGradient>
            </defs>
            {PIEZAS.map((p, n) => (
              <g key={p.titulo}>
                <path d={curva(p)} className="tc-linea" />
                <path d={curva(p)} pathLength={100} className="tc-pulso" style={{ animationDelay: `${n * 0.45}s` }} />
              </g>
            ))}
          </svg>

          <div className="tc-centro" style={{ left: '50%', top: '50%' }}>
            <div className="tc-centro-icono"><Link2 /></div>
            <div>
              <p className="tc-centro-titulo">Tu consultorio</p>
              <p className="tc-centro-link">consultarapp.com/p/tu-nombre</p>
            </div>
          </div>

          {PIEZAS.map((p, n) => (
            <div
              key={p.titulo}
              className={`tc-pieza${p.destacada ? ' tc-pieza--destacada' : ''}`}
              style={{ left: `${(p.x / 960) * 100}%`, top: `${(p.y / 440) * 100}%`, ['--i' as string]: n }}
            >
              <div className="tc-pieza-icono">{p.icono}</div>
              <div className="min-w-0">
                <p className="tc-pieza-titulo">{p.titulo}</p>
                <p className="tc-pieza-detalle">{p.detalle}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Celular y tablet: las mismas piezas en columna, unidas por una línea */}
        <div className="tc-columna lg:hidden">
          <div className="tc-centro tc-centro--columna">
            <div className="tc-centro-icono"><Link2 /></div>
            <div>
              <p className="tc-centro-titulo">Tu consultorio</p>
              <p className="tc-centro-link">consultarapp.com/p/tu-nombre</p>
            </div>
          </div>
          <div className="tc-lista-caja">
            <span className="tc-espina" aria-hidden="true"><span className="tc-espina-pulso" /></span>
            <ul className="tc-lista" aria-label="Lo que queda conectado">
              {PIEZAS.map((p, n) => (
                <li key={p.titulo} className={`tc-pieza tc-pieza--fila${p.destacada ? ' tc-pieza--destacada' : ''}`} style={{ ['--i' as string]: n }}>
                  <div className="tc-pieza-icono">{p.icono}</div>
                  <div className="min-w-0">
                    <p className="tc-pieza-titulo">{p.titulo}</p>
                    <p className="tc-pieza-detalle">{p.detalle}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Para lectores de pantalla en escritorio (el esquema es decorativo;
            en el celular ya está la lista de arriba) */}
        <ul className="sr-only hidden lg:block">
          {PIEZAS.map((p) => <li key={p.titulo}>{p.titulo}: {p.detalle}</li>)}
        </ul>

        {/* Beneficios de tenerlo todo enlazado */}
        <div className="mt-14 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFICIOS.map((b, n) => (
            <div key={b.titulo} className="tc-beneficio" style={{ ['--i' as string]: n }}>
              <div className="tc-beneficio-icono">{b.icono}</div>
              <h3 className="tc-beneficio-titulo">{b.titulo}</h3>
              <p className="tc-beneficio-texto">{b.texto}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-slate-300 text-lg">
          Todo esto, incluido por <span className="font-bold text-white">{precio}</span> al mes.{' '}
          {alVerPlan && (
            <button type="button" onClick={alVerPlan} className="tc-ver-plan">
              Ver el plan
            </button>
          )}
        </p>
      </div>
    </section>
  );
};

export default TodoConectado;
